/**
 * Clipboard wire format (12 byte header + payload):
 *   0    version u8 (=1)
 *   1    op u8 (0=Set, 1=Request, 2=Ack)
 *   2    format u8 (0=Utf8Text, 1=Png, 2=FileList)
 *   3    reserved u8
 *   4-7  origin_tag u32 BE (echo detection)
 *   8-11 payload_len u32 BE
 *   12+  payload
 *
 * PacketHeader(11) + ClipboardFrame(12+payload), channel = Clipboard (4).
 * Echo detection: sent origin_tag'i hatırla; geri gelende aynıysa drop.
 */
import { Channel, PacketType, buildPacket, } from './packet-header';
const MAX_PAYLOAD = 64 * 1024;
const ORIGIN_TAG = Math.floor(Math.random() * 0xffffffff);
export const ClipboardOp = { Set: 0, Request: 1, Ack: 2 };
export const ClipboardFormat = { Utf8Text: 0, Png: 1, FileList: 2 };
export class ClipboardBroker {
    bridge;
    seq = 1;
    sessionId24;
    sentTags = new Set();
    lastSetText = null;
    /**
     * @param sessionId24 Backend'den gelen relay session ID (24-bit).
     */
    constructor(bridge, sessionId24) {
        this.bridge = bridge;
        this.sessionId24 = sessionId24 & 0x00ffffff;
    }
    /** Gelen host clipboard set'ini lokal panoya uygula. */
    handleIncoming(data) {
        if (data.length < 12)
            return;
        const op = data[1];
        const format = data[2];
        const originTag = (data[4] << 24) | (data[5] << 16) | (data[6] << 8) | data[7];
        const payloadLen = (data[8] << 24) | (data[9] << 16) | (data[10] << 8) | data[11];
        if (data.length < 12 + payloadLen)
            return;
        // Kendi gönderdiğimiz mesaj yankılandıysa drop (sonsuz döngü önler)
        if (this.sentTags.has(originTag))
            return;
        if (op === ClipboardOp.Set && format === ClipboardFormat.Utf8Text) {
            const text = new TextDecoder('utf-8').decode(data.subarray(12, 12 + payloadLen));
            if (text && text !== this.lastSetText) {
                this.lastSetText = text;
                void navigator.clipboard.writeText(text).catch(() => { });
            }
        }
    }
    /** Lokal panodaki metni host'a yolla. */
    async sendClipboardText(text) {
        if (!text)
            return;
        const body = new TextEncoder().encode(text);
        if (body.length > MAX_PAYLOAD)
            return;
        const frame = new Uint8Array(12 + body.length);
        frame[0] = 1; // version
        frame[1] = ClipboardOp.Set;
        frame[2] = ClipboardFormat.Utf8Text;
        frame[3] = 0;
        const tag = (ORIGIN_TAG + this.seq) & 0xffffffff;
        frame[4] = (tag >>> 24) & 0xff;
        frame[5] = (tag >>> 16) & 0xff;
        frame[6] = (tag >>> 8) & 0xff;
        frame[7] = tag & 0xff;
        frame[8] = (body.length >>> 24) & 0xff;
        frame[9] = (body.length >>> 16) & 0xff;
        frame[10] = (body.length >>> 8) & 0xff;
        frame[11] = body.length & 0xff;
        frame.set(body, 12);
        this.sentTags.add(tag);
        // Temizlik: 200 tag sonrası en eskileri unut
        if (this.sentTags.size > 200) {
            const first = this.sentTags.values().next().value;
            if (first !== undefined)
                this.sentTags.delete(first);
        }
        this.lastSetText = text;
        const packet = buildPacket({
            version: 0,
            packetType: PacketType.Data,
            flags: 0,
            sessionId: this.sessionId24,
            sequence: this.seq++,
            channel: Channel.Clipboard,
        }, frame);
        this.bridge.send(packet);
    }
    /**
     * Tarayıcı pano değişimini host'a aktarmak için periyodik poll
     * (Permissions API permission gerektirir; kullanıcı izin vermezse no-op).
     */
    startPollLoop(intervalMs = 500) {
        let stopped = false;
        const tick = async () => {
            if (stopped)
                return;
            try {
                const text = await navigator.clipboard.readText();
                if (text && text !== this.lastSetText) {
                    await this.sendClipboardText(text);
                }
            }
            catch {
                // Clipboard API izin vermez → poll'u durdur
                stopped = true;
                return;
            }
            setTimeout(() => void tick(), intervalMs);
        };
        void tick();
        return () => { stopped = true; };
    }
}
